import styled from '@emotion/styled'
import {
  ANY_ITEM,
  COPY,
  DO_NOTHING,
  EMPTYGROUP,
  GROUP_MAX_ITEMS,
  modalEndConditionState,
  MOVE
} from 'Hooks/useModalEndCondition/useModalEndCondition'
import debounce from 'lodash.debounce'
import {
  Button,
  BUTTON_VARIANT_PRIMARY,
  Flex,
  FlexItem,
  FormElement,
  Icon,
  InputText,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Loading,
  Panel,
  RadioButton,
  Select,
  SPACING_16,
  SPACING_8,
  SpacingVertical,
  Text,
  TEXT_VARIANT_LIGHT,
  Title,
  TITLE_VARIANT_M,
  useAsyncSelect
} from 'mailup-web-components'

import {
  addGroupCallback,
  errorCallback
} from 'Pages/Task/recoil/callbacks/callbacks'
import { ACTION_MOVE_COPY, taskState } from 'Pages/Task/recoil/state/task'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  getGroupById,
  getGroups
} from 'Pages/Task/recoil/actions/asyncSearchStep2'
import { useRecoilCallback, useRecoilState } from 'recoil'

const StyledIcon = styled(Icon)`
  color: var(${({ disabled }) => (disabled ? '--silver-chalice' : '--java')});
`

const SELECT_TYPE_COPY = 0
const SELECT_TYPE_MOVE = 1
const SELECT_TYPE_MOVE_TO = 2
const SELECT_TYPE_EMPTY = 3

const CreateNewGroup = ({
  fetchGroups,
  enableWhenSectionIs,
  section,
  action
}) => {
  const intl = useIntl()
  const [expanded, setExpanded] = useState(false)
  const [name, setName] = useState('')
  const addGroup = useRecoilCallback(addGroupCallback, [])
  const inputRef = useRef(null)
  const handleChange = (e) => {
    setName(e.target.value)
  }

  const handleAddGroup = async () => {
    const notes = ''
    const testGroups = false
    await addGroup({ name, notes, testGroups })
    await fetchGroups('')
    setName('')
    setExpanded(false)
  }

  useEffect(() => {
    setName('')
    setExpanded(false)
  }, [section])

  return (
    <div>
      <Link
        disabled={enableWhenSectionIs !== section}
        onClick={() => {
          setExpanded(!expanded)
        }}
        variant={LINK_VARIANT_HIGHLIGHT}
      >
        <Flex columnGap='4px'>
          <StyledIcon
            disabled={enableWhenSectionIs !== section}
            family='fa'
            icon={expanded ? 'minus' : 'plus'}
          />
          {intl.formatMessage({
            id: 'Load.MailupCommon.AddNewGroup'
          })}
        </Flex>
      </Link>

      {expanded && (
        <Flex
          maxWidth={action === ACTION_MOVE_COPY ? '50%' : '324px'}
          columnGap='8px'
        >
          <FlexItem flexGrow='1'>
            <FormElement>
              <InputText
                ref={inputRef}
                value={name}
                onChange={handleChange}
                placeholder={intl.formatMessage({
                  id: 'Load.Task.ActionsSegmentAddGroupPlaceholder'
                })}
              />
            </FormElement>
          </FlexItem>
          <FlexItem>
            <Button
              kind={BUTTON_VARIANT_PRIMARY}
              onClick={handleAddGroup}
              disabled={!name}
              data-mu-test='add-new-group'
            >
              {intl.formatMessage({ id: 'Load.NewGroup.AddGroup' })}
            </Button>
          </FlexItem>
        </Flex>
      )}
    </div>
  )
}

const RadioCondition = ({ check, setCheck, condition, label }) => {
  const intl = useIntl()
  return (
    <div>
      <RadioButton
        value={condition}
        checked={check === condition}
        label={intl.formatMessage({
          id: label
        })}
        onChange={() => {
          setCheck((prev) => ({
            ...prev,
            check: condition,
            selectedGroups: [],
            selectedGroupsTo: [],
            IsDiry: true
          }))
        }}
        data-mu-test={`task-2-condition-radio-${condition}`}
      />
    </div>
  )
}

export const RecipinetsManagement = ({ fetchGroups, fetchGroup }) => {
  const intl = useIntl()

  const [
    {
      loading,
      loaded,
      error,
      check,
      copyValue,
      emptyValue,
      moveToValue,
      moveFromValue,
      loadGroup
    },
    setState
  ] = useRecoilState(modalEndConditionState)

  // gruppi
  const [search, setSearch] = useState({ type: -1, valueToSearch: '' })

  // eslint-disable-next-line
  const [task, setTask] = useRecoilState(taskState)

  const displayErrorMessage =
    check === MOVE && moveToValue && moveFromValue
      ? moveToValue.value === moveFromValue.value &&
        moveFromValue.value !== -1 &&
        moveToValue.value !== -1
      : false

  const isLoading = (type) => {
    return search.type === type
  }

  // async search

  const hints = {
    loading: intl.formatMessage({ id: 'Load.MailupCommon.InputStatusLoading' }),
    moreResults: intl.formatMessage({
      id: 'Load.MailupCommon.InputStatusTooManyItems'
    }),
    noResults: intl.formatMessage({ id: 'Load.Common.NoResultChosen' })
  }

  const getInitialItems = () => getGroups()
  const getItemsByTerm = (searchBy) => getGroups(searchBy)
  const getItemById = (idG) => getGroupById(idG)
  const onError = useRecoilCallback(errorCallback, [])

  // select copy
  const {
    options: copyOpts,
    loading: copyLoading,
    onSearch: onSearchCopy,
    onChange: onChangeCopy,
    ...copySelectProps
  } = useAsyncSelect({
    initialValue: check === COPY && copyValue && copyValue.value,
    getInitialItems,
    getItemsByTerm,
    getItemById,
    onError,
    hints,
    maxItems: GROUP_MAX_ITEMS
  })

  // empty copy
  const {
    options: emptyOpts,
    loading: emptyLoading,
    onSearch: onSearchEmpty,
    onChange: onChangeEmpty,
    ...emptySelectProps
  } = useAsyncSelect({
    initialValue: check === EMPTYGROUP && emptyValue && emptyValue.value,
    getInitialItems,
    getItemsByTerm,
    getItemById,
    onError,
    hints,
    maxItems: GROUP_MAX_ITEMS
  })

  // select move from
  const {
    options: moveFromOpts,
    loading: moveFromLoading,
    onSearch: onSearchMoveFrom,
    onChange: onChangeMoveFrom,
    ...moveFromSelectProps
  } = useAsyncSelect({
    initialValue: check === MOVE && moveFromValue && moveFromValue.value,
    getInitialItems,
    getItemsByTerm,
    getItemById: getGroupById,
    onError,
    hints,
    maxItems: GROUP_MAX_ITEMS
  })

  // select move to
  const {
    options: moveToOpts,
    loading: moveToLoading,
    onSearch: onSearchMoveTo,
    onChange: onChangeMoveTo,
    ...moveToSelectProps
  } = useAsyncSelect({
    initialValue: check === MOVE && moveToValue && moveToValue.value,
    getInitialItems,
    getItemsByTerm,
    getItemById: getGroupById,
    onError,
    hints,
    maxItems: GROUP_MAX_ITEMS
  })

  const withAnyGroup = useMemo(() => {
    return [ANY_ITEM, ...moveFromOpts]
  }, [moveFromOpts])

  const handleChangeCopy = async (value) => {
    setSearch((prev) => ({
      ...prev,
      type: SELECT_TYPE_COPY
    }))
    const group = await getGroupById(value)
    setState((prev) => ({
      ...prev,
      copyValue: group,
      emptyValue: { label: '', value: -1 },
      moveToValue: { label: '', value: -1 },
      moveFromValue: { label: '', value: -1 },
      selectedGroups: [group],
      selectCollapsed: true,
      IsDiry: true
    }))
    onChangeCopy(value)
  }

  const handleChangeEmpty = async (value) => {
    setSearch((prev) => ({
      ...prev,
      type: SELECT_TYPE_EMPTY
    }))
    const group = await getGroupById(value)
    setState((prev) => ({
      ...prev,
      emptyValue: group,
      copyValue: { label: '', value: -1 },
      moveToValue: { label: '', value: -1 },
      moveFromValue: { label: '', value: -1 },
      selectedGroups: [group],
      selectCollapsed: true,
      IsDiry: true
    }))
    onChangeEmpty(value)
  }
  const handleChangeMoveFrom = async (value) => {
    const group = value === 0 ? ANY_ITEM : await getGroupById(value)

    setState((prev) => ({
      ...prev,
      moveFromValue: group,
      copyValue: { label: '', value: -1 },
      emptyValue: { label: '', value: -1 },
      selectedGroups: [group],
      selectCollapsed: true,
      IsDiry: true
    }))
    setSearch((prev) => ({
      ...prev,
      type: SELECT_TYPE_MOVE
    }))

    onChangeMoveFrom(value)
  }

  const handleChangeMoveTo = async (value) => {
    const group = await getGroupById(value)

    setSearch((prev) => ({
      ...prev,
      type: SELECT_TYPE_MOVE_TO
    }))

    setState((prev) => ({
      ...prev,
      moveToValue: group,
      copyValue: { label: '', value: -1 },
      emptyValue: { label: '', value: -1 },
      selectedGroupsTo: [group],
      selectCollapsed: true,
      IsDiry: true
    }))
    onChangeMoveTo(value)
  }

  const { current: debouncedOnSearchCopy } = useRef(debounce(onSearchCopy, 350))
  const { current: debouncedOnSearchMoveFrom } = useRef(
    debounce(onSearchMoveFrom, 350)
  )
  const { current: debouncedOnSearchMoveTo } = useRef(
    debounce(onSearchMoveTo, 350)
  )

  const { current: debouncedOnSearchEmpty } = useRef(
    debounce(onSearchEmpty, 350)
  )

  const handleSearchCopy = (s) => {
    setSearch({ type: SELECT_TYPE_COPY, valueToSearch: s })
    debouncedOnSearchCopy.cancel()
    debouncedOnSearchCopy(s)
    return s
  }

  const handleSearchEmpty = (s) => {
    setSearch({ type: SELECT_TYPE_EMPTY, valueToSearch: s })
    debouncedOnSearchEmpty.cancel()
    debouncedOnSearchEmpty(s)
    return s
  }

  const handleSearchMoveFrom = (s) => {
    setSearch({ type: SELECT_TYPE_MOVE, valueToSearch: s })
    debouncedOnSearchMoveFrom.cancel()
    debouncedOnSearchMoveFrom(s)
    return s
  }

  const handleSearchMoveTo = (s) => {
    setSearch({ type: SELECT_TYPE_MOVE_TO, valueToSearch: s })
    debouncedOnSearchMoveTo.cancel()
    debouncedOnSearchMoveTo(s)
    return s
  }

  return (
    <>
      <Loading open={loading} />

      {!error && loaded && (
        <Panel>
          <Title variant={TITLE_VARIANT_M}>
            {intl.formatMessage({
              id: 'Load.Task.ActionsSubSegmentPanelTitle'
            })}
          </Title>
          <>
            {task.Detail.Action !== ACTION_MOVE_COPY && (
              <>
                <SpacingVertical />
                <RadioCondition
                  check={check}
                  setCheck={setState}
                  condition={DO_NOTHING}
                  label='Load.Task.ActionsSegmentDoNothing'
                />
              </>
            )}
          </>

          <SpacingVertical variant={SPACING_16} />

          <RadioCondition
            check={check}
            setCheck={setState}
            condition={COPY}
            label='Load.Task.ActionsSegmentCopy'
          />

          {parseInt(check) === SELECT_TYPE_MOVE && (
            <Flex margin='0 0 0 24px' flexDirection='column'>
              <SpacingVertical variant={SPACING_8} />
              <FlexItem
                maxWidth={
                  task.Detail.Action === ACTION_MOVE_COPY ? '50%' : '324px'
                }
              >
                <Select
                  value={check === COPY && copyValue && copyValue.value}
                  searchable
                  onChange={handleChangeCopy}
                  onSearch={handleSearchCopy}
                  options={copyOpts}
                  disabled={check !== COPY}
                  loading={
                    (loadGroup && isLoading(SELECT_TYPE_COPY)) || copyLoading
                  }
                  placeholder={intl.formatMessage({
                    id: 'Load.MailupCommon.SelectGroup'
                  })}
                  {...copySelectProps}
                />
              </FlexItem>
              <SpacingVertical variant={SPACING_8} />
              <CreateNewGroup
                fetchGroups={fetchGroups}
                section={check}
                enableWhenSectionIs={COPY}
                action={task.Detail.Action}
              />
            </Flex>
          )}
          <SpacingVertical variant={SPACING_16} />
          <RadioCondition
            check={check}
            setCheck={setState}
            condition={MOVE}
            label='Load.Task.ActionsSegmentMove'
          />

          {parseInt(check) === SELECT_TYPE_MOVE_TO && (
            <Flex margin='0 0 0 24px' flexDirection='column'>
              <SpacingVertical variant={SPACING_8} />
              <Text variant={TEXT_VARIANT_LIGHT}>
                {intl.formatMessage({
                  id: 'Load.Task.ActionSegmentMoveDescription'
                })}
              </Text>
              <SpacingVertical variant={SPACING_8} />

              <FlexItem
                maxWidth={
                  task.Detail.Action === ACTION_MOVE_COPY ? '50%' : '324px'
                }
              >
                <FormElement
                  label={intl.formatMessage({
                    id: 'Load.Task.ActionsSegmentMovePrevGroup'
                  })}
                >
                  <Select
                    value={
                      check === MOVE && moveFromValue && moveFromValue.value
                    }
                    searchable
                    onChange={handleChangeMoveFrom}
                    onSearch={handleSearchMoveFrom}
                    options={withAnyGroup}
                    disabled={check !== MOVE}
                    loading={
                      (loadGroup && isLoading(SELECT_TYPE_MOVE)) ||
                      moveFromLoading
                    }
                    placeholder={intl.formatMessage({
                      id: 'Load.MailupCommon.SelectGroup'
                    })}
                    {...moveFromSelectProps}
                  />
                </FormElement>
              </FlexItem>

              <SpacingVertical variant={SPACING_8} />
              <FlexItem
                maxWidth={
                  task.Detail.Action === ACTION_MOVE_COPY ? '50%' : '324px'
                }
              >
                <FormElement
                  label={intl.formatMessage({
                    id: 'Load.Task.ActionsSegmentMoveDestGroup'
                  })}
                  feedback={
                    displayErrorMessage
                      ? intl.formatMessage({
                          id: 'Load.AutomationWorkflow.EndConditionMoveMessageError'
                        })
                      : undefined
                  }
                  feedbackVariant={displayErrorMessage ? 'danger' : undefined}
                >
                  <Select
                    value={check === MOVE && moveToValue && moveToValue.value}
                    searchable
                    onChange={handleChangeMoveTo}
                    options={moveToOpts}
                    onSearch={handleSearchMoveTo}
                    disabled={check !== MOVE}
                    loading={
                      (loadGroup && isLoading(SELECT_TYPE_MOVE_TO)) ||
                      moveToLoading
                    }
                    placeholder={intl.formatMessage({
                      id: 'Load.MailupCommon.SelectGroup'
                    })}
                    error={displayErrorMessage}
                    {...moveToSelectProps}
                  />
                </FormElement>
              </FlexItem>
              <SpacingVertical variant={SPACING_8} />

              <CreateNewGroup
                fetchGroups={fetchGroups}
                section={check}
                enableWhenSectionIs={MOVE}
                action={task.Detail.Action}
              />
            </Flex>
          )}
          {task.Detail.Action !== ACTION_MOVE_COPY && (
            <>
              <SpacingVertical variant={SPACING_16} />
              <RadioCondition
                check={check}
                setCheck={setState}
                condition={EMPTYGROUP}
                label='Load.Task.ActionsSegmentEmptyGroup'
              />
            </>
          )}
          {parseInt(check) === SELECT_TYPE_EMPTY && (
            <Flex margin='0 0 0 24px' flexDirection='column'>
              <SpacingVertical variant={SPACING_8} />
              <FlexItem
                maxWidth={
                  task.Detail.Action === ACTION_MOVE_COPY ? '50%' : '324px'
                }
              >
                <Select
                  value={check === EMPTYGROUP && emptyValue && emptyValue.value}
                  searchable
                  onChange={handleChangeEmpty}
                  onSearch={handleSearchEmpty}
                  options={emptyOpts}
                  disabled={check !== EMPTYGROUP}
                  loading={
                    (loadGroup && isLoading(ACTION_MOVE_COPY)) || emptyLoading
                  }
                  placeholder={intl.formatMessage({
                    id: 'Load.MailupCommon.SelectGroup'
                  })}
                  {...emptySelectProps}
                />
              </FlexItem>
            </Flex>
          )}
        </Panel>
      )}
    </>
  )
}

RecipinetsManagement.propTypes = {
  fetchGroups: PropTypes.func,
  fetchGroup: PropTypes.func
}

RadioCondition.propTypes = {
  check: PropTypes.string,
  condition: PropTypes.string,
  setCheck: PropTypes.func,
  label: PropTypes.string
}

CreateNewGroup.propTypes = {
  fetchGroups: PropTypes.func,
  enableWhenSectionIs: PropTypes.string,
  section: PropTypes.string,
  action: PropTypes.any
}

export default RecipinetsManagement
